import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import useLatestRef from "../react/react-use/useLatestRef";
import React, { useCallback, useEffect, useState } from "react";
import { useGroopitApiContext, useGroupContext } from "groopit-js";
import {
  GroopitFilter,
  UnnamedGroopitFilterDisplayDTO,
} from "./GroopitFilterDTO";
import {
  ErrorDTO,
  ErrorType,
  NamedGroopitFilterDisplayDTO,
  NamedGroopitFilterUpdateDTO,
} from "groopit-js/client";
import { useFilterContext } from "./State/FilterContext";
import { useFilterDispatchContext } from "./State/FilterDispatchContext";
import {
  createSavedFiltersAddAction,
  createSavedFiltersUpdateAction,
} from "./State/SavedFiltersActions";
import { createFilterReplaceFilterAction } from "./State/FilterActions";
import { usePrev } from "@react-spring/shared";
import { GroopitFilterDisplay } from "../Models/GroopitFilterDisplay";

interface SaveFilterDialogProps extends DialogProps {
  filter: GroopitFilter;
  mode: "save" | "rename" | "save_as" | "publish";
  onClose: {
    bivarianceHack(
      // eslint-disable-next-line @typescript-eslint/ban-types
      event: {},
      reason: "backdropClick" | "escapeKeyDown" | "dialogRequested",
    ): void;
  }["bivarianceHack"];
}

export const SaveFilterDialog = (props: SaveFilterDialogProps) => {
  const { filter, mode, onClose, open } = props;
  const { user_id, owner } = useGroupContext();
  const rename =
    mode === "rename" ||
    mode === "save_as" ||
    filter instanceof UnnamedGroopitFilterDisplayDTO ||
    !filter.name ||
    (!owner && filter.creator.id !== user_id);
  const api = useLatestRef(useGroopitApiContext());
  const { filter: activeFilter } = useFilterContext();
  const { filterDispatcher, savedFiltersDispatcher } =
    useFilterDispatchContext();
  const { group } = useGroupContext();
  const [filterName, setFilterName] = useState("");
  useEffect(() => {
    setFilterName(
      filter instanceof NamedGroopitFilterDisplayDTO ? filter.name : "",
    );
  }, [filter]);
  const [error, setError] = useState<ErrorDTO>();
  const [isSaving, setIsSaving] = useState(false);

  const saveFilter = useCallback(
    (filterName: string) => {
      setIsSaving(true);
      setError(undefined);
      (mode != "save_as" && filter instanceof NamedGroopitFilterDisplayDTO
        ? api.current.groop
            .updateGroopitFilter(
              group.id,
              filter.id,
              new NamedGroopitFilterUpdateDTO({
                filter: GroopitFilterDisplay.convertToGroopitFilter(
                  filter.filter,
                ),
                name: filterName,
                isPublished: filter.isPublished || mode === "publish",
              }),
            )
            .then((filter) => {
              savedFiltersDispatcher(createSavedFiltersUpdateAction(filter));
              return filter;
            })
        : api.current.groop
            .createGroopitFilter(
              group.id,
              new NamedGroopitFilterUpdateDTO({
                name: filterName,
                isPublished: mode === "publish",
                filter: GroopitFilterDisplay.convertToGroopitFilter(
                  filter.filter,
                ),
              }),
            )
            .then((filter) => {
              savedFiltersDispatcher(createSavedFiltersAddAction(filter));
              return filter;
            })
      )
        .then((filter) => {
          if (
            activeFilter instanceof UnnamedGroopitFilterDisplayDTO ||
            filter.id === activeFilter.id
          ) {
            filterDispatcher(createFilterReplaceFilterAction(filter));
          }
          onClose({}, "dialogRequested");
        })
        .catch((error) => {
          setError(ErrorDTO.fromJS(error));
        })
        .finally(() => setIsSaving(false));
    },
    [
      filter,
      api,
      group.id,
      mode,
      savedFiltersDispatcher,
      activeFilter,
      onClose,
      filterDispatcher,
    ],
  );

  const previousOpen = usePrev(open);
  useEffect(() => {
    // If we're moving from closed to open and we're not forcing a rename
    // (and the filter is already named) we can kick off the save
    // immediately
    if (!previousOpen && open) {
      if (!rename && open && filter instanceof NamedGroopitFilterDisplayDTO) {
        saveFilter(filter.name);
      }
    }
  }, [filter, open, previousOpen, rename, saveFilter]);

  return (
    <Dialog
      {...props}
      fullWidth
      maxWidth="sm"
      aria-labelledby="name-filter-title"
    >
      <DialogTitle id="name-filter-title">
        {rename && filter instanceof NamedGroopitFilterDisplayDTO
          ? "Rename"
          : mode === "publish"
          ? "Publish"
          : "Save"}{" "}
        filter
      </DialogTitle>
      <DialogContent>
        {isSaving ? (
          <div className="text-center">
            <CircularProgress />
          </div>
        ) : (
          <>
            {(rename ||
              (error && error.error === ErrorType.UniquenessConflict)) && (
              <>
                <TextField
                  id="filter-name"
                  fullWidth
                  variant="outlined"
                  label="Name your filter"
                  value={filterName}
                  inputProps={{ maxLength: 150 }}
                  onChange={(e) => setFilterName(e.target.value)}
                />
                <div className="flex flex-row-reverse text-display-caption">
                  <span>{filterName.length} / 150</span>
                </div>
              </>
            )}
            {error?.error && (
              <div className="text-display-error mt-2">
                {error.error === ErrorType.UniquenessConflict
                  ? "A filter with this name already exists. Please choose a different name."
                  : error.error === ErrorType.HiddenResourceConflict
                  ? "This saved filter contains filter on one or more hidden fields and therefore cannot be published to the group. Please remove the filter on the hidden field(s) if you wish to make this saved filter available to the entire group."
                  : "An error occurred while saving the filter. Please try again later."}
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="default"
          variant="outlined"
          onClick={(event) => {
            onClose(event, "dialogRequested");
            event.stopPropagation();
          }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={
            isSaving ||
            !filterName ||
            error?.error === ErrorType.HiddenResourceConflict
          }
          onClick={(event) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            saveFilter(filterName!);
            event.stopPropagation();
          }}
        >
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
};
